import React from 'react';

import { useIntl } from 'react-intl';

import { fg } from '@atlaskit/platform-feature-flags';
import { MenuList } from '@atlaskit/side-nav-items/menu-list';
import { L1_MENU_GROUPS } from '@atlassian/navigation-common';
import {
	createElementRegistry,
	ElementRegistryProvider,
	GroupDropIndicatorWithDragOver,
	isL1ItemData,
	useDragState,
} from '@atlassian/navigation-drag-and-drop';
import { NavErrorBoundary } from '@atlassian/navigation-error-boundary';

import type { GlobalSideNavigationProps } from '../../common/types';
import NavItem from '../../common/ui/nav-item';
import {
	type ItemsAndShortcutsProviderProps,
	useItemsAndShortcuts,
} from '../../controllers/items-and-shortcuts-provider';
import { type OptionsProviderProps, useOptions } from '../../controllers/options-provider';
import { useDragAndDropSave } from '../../controllers/use-drag-and-drop-save';
import { useMonitorForDnDElements } from '../../controllers/use-monitor-for-dnd-elements';
import { useUserMenuDataShapes } from '../../controllers/user-state/user-menu-data-shapes-provider';
import { GLOBAL_SIDE_NAV_TEST_ID } from '../constants';
import { GSNSideNavigationLayout } from '../layout/gsn-layout';
import { PostMainItems } from '../post-main-items';

import { defaultErrorMessages, messages, mainNavItemMessages } from './messages';

export type SideNavProps = Omit<
	GlobalSideNavigationProps,
	keyof OptionsProviderProps | keyof ItemsAndShortcutsProviderProps | 'peekMenuId'
>;

export function SideNav(props: SideNavProps): React.JSX.Element {
	const {
		postItemsElem,
		postMainElem,
		openShortcutsInSameTab,
		viewAllAppsConfig,
		ShortcutsSlot,
		experimental_onShortcutsMenuSettled,
		onPeekEnd,
		onPeekStart,
		onLeftNavCollapse,
		onLeftNavExpand,
		onResizeEnd,
		onResizeStart,
		sideNavContentRef,
	} = props;

	const { app, intlMode, l1DragAndDropEnabled } = useOptions();
	const { allItems, shortcuts } = useItemsAndShortcuts();
	const userMenuDataShapes = useUserMenuDataShapes();
	const { formatMessage } = useIntl();

	// #region Drag and drop
	// Element registry for L1 items
	const [elementRegistry] = React.useState(createElementRegistry);
	const { dragAndDropSave } = useDragAndDropSave(userMenuDataShapes, elementRegistry);

	const [draggingL1Group, setDraggingL1Group] = React.useState<string | null>(null);

	React.useEffect(() => {
		if (!experimental_onShortcutsMenuSettled) {
			return;
		}
		experimental_onShortcutsMenuSettled({
			shortcuts: shortcuts ?? {},
			visibleMenuIds: userMenuDataShapes.visibleMenuIds,
		});
	}, [shortcuts, userMenuDataShapes.visibleMenuIds, experimental_onShortcutsMenuSettled]);

	useMonitorForDnDElements({
		l1DragAndDropEnabled,
		dragAndDropSave,
		setDraggingL1Group,
		hiddenMenuIds: userMenuDataShapes.hiddenMenuIds,
	});

	const { groupDropIndicatorRef, dragSource } = useDragState({
		dragAndDropEnabled: l1DragAndDropEnabled,
		isItemData: isL1ItemData,
	});
	// #endregion

	const shouldUseErrorMessages = fg('navx-4866-migrate-messages-from-nav-error-boundary');

	// #region Core rendering
	const mainNavItems = React.useMemo(
		() =>
			userMenuDataShapes.visibleItemMenuIds.map((id) => (
				<NavErrorBoundary
					key={id}
					errorMessages={shouldUseErrorMessages ? mainNavItemMessages : undefined}
					errorMessageValues={{ menuId: id }}
					intlMode={intlMode}
					location={shouldUseErrorMessages ? undefined : `gsn-main-nav-item--${id}`}
					sentryConfig={{ product: app }}
					showFlags
					{...(fg('navx-gsn-dnc-ownership-marker-navitem') && {
						UNSAFE_wrapper: props.UNSAFE_wrapper,
					})}
				>
					<NavItem key={id} id={id} item={allItems[id]} />
				</NavErrorBoundary>
			)),
		[
			userMenuDataShapes.visibleItemMenuIds,
			app,
			allItems,
			intlMode,
			props.UNSAFE_wrapper,
			shouldUseErrorMessages,
		],
	);

	const topElem = React.useMemo(
		() =>
			props.topElem ? (
				<NavErrorBoundary
					errorMessages={shouldUseErrorMessages ? defaultErrorMessages : undefined}
					intlMode={intlMode}
					location={shouldUseErrorMessages ? undefined : 'gsn-top-element'}
					sentryConfig={{ product: app }}
					showFlags
					{...(fg('navx-gsn-dnc-ownership-marker-topelement') && {
						UNSAFE_wrapper: props.UNSAFE_wrapper,
					})}
				>
					{props.topElem}
				</NavErrorBoundary>
			) : null,
		[app, props.topElem, intlMode, props.UNSAFE_wrapper, shouldUseErrorMessages],
	);

	const bottomElem = React.useMemo(
		() =>
			props.bottomElem ? (
				<NavErrorBoundary
					errorMessages={shouldUseErrorMessages ? defaultErrorMessages : undefined}
					intlMode={intlMode}
					location={shouldUseErrorMessages ? undefined : 'gsn-bottom-element'}
					sentryConfig={{ product: app }}
					// errorMessages=... use the default error messages
					showFlags
					{...(fg('navx-gsn-dnc-ownership-marker-bottomelement') && {
						UNSAFE_wrapper: props.UNSAFE_wrapper,
					})}
				>
					{props.bottomElem}
				</NavErrorBoundary>
			) : null,
		[app, intlMode, props.bottomElem, props.UNSAFE_wrapper, shouldUseErrorMessages],
	);

	const preMainElem = React.useMemo(
		() =>
			props.preMainElem ? (
				<NavErrorBoundary
					errorMessages={shouldUseErrorMessages ? defaultErrorMessages : undefined}
					intlMode={intlMode}
					location={shouldUseErrorMessages ? undefined : 'gsn-pre-main-element'}
					sentryConfig={{ product: app }}
					showFlags
					{...(fg('navx-gsn-dnc-ownership-marker-topelement') && {
						UNSAFE_wrapper: props.UNSAFE_wrapper,
					})}
				>
					{props.preMainElem}
				</NavErrorBoundary>
			) : null,
		[app, intlMode, props.preMainElem, props.UNSAFE_wrapper, shouldUseErrorMessages],
	);

	return (
		<ElementRegistryProvider registry={elementRegistry}>
			<GSNSideNavigationLayout
				testId={props.testId ?? GLOBAL_SIDE_NAV_TEST_ID}
				preMainElem={topElem}
				onCollapse={onLeftNavCollapse}
				onExpand={onLeftNavExpand}
				onPeekEnd={onPeekEnd}
				onPeekStart={onPeekStart}
				onResizeEnd={onResizeEnd}
				onResizeStart={onResizeStart}
				sideNavContentRef={sideNavContentRef}
				postMainElem={
					<NavErrorBoundary
						errorMessages={shouldUseErrorMessages ? defaultErrorMessages : undefined}
						intlMode={intlMode}
						location={shouldUseErrorMessages ? undefined : 'gsn-post-main-items'}
						sentryConfig={{ product: app }}
						showFlags
						{...(fg('navx-gsn-dnc-ownership-marker-postmainitems') && {
							UNSAFE_wrapper: props.UNSAFE_wrapper,
						})}
					>
						<PostMainItems
							postItemsElem={postItemsElem}
							postMainElem={postMainElem}
							draggingL1Group={draggingL1Group}
							openShortcutsInSameTab={openShortcutsInSameTab}
							viewAllAppsConfig={viewAllAppsConfig}
							ShortcutsSlot={ShortcutsSlot}
						/>
					</NavErrorBoundary>
				}
				bottomElem={bottomElem}
			>
				{mainNavItems?.length ? (
					<MenuList>
						{preMainElem}
						<GroupDropIndicatorWithDragOver
							ref={groupDropIndicatorRef}
							isActive={draggingL1Group === L1_MENU_GROUPS.MENU}
							ariaLabel={formatMessage(messages.primaryNavigationSectionScreenReader, {
								item: dragSource,
							})}
						>
							{mainNavItems}
						</GroupDropIndicatorWithDragOver>
					</MenuList>
				) : null}
			</GSNSideNavigationLayout>
		</ElementRegistryProvider>
	);
	// #endregion
}
